import { useState } from 'react';

interface Estimate {
  id: string;
  created_at: string;
  full_name: string;
  phone: string;
  email: string;
  vehicle: string;
  insurance: string;
  message: string;
  status: string;
  signed_photo_urls: string[];
}

interface EstimateCardProps {
  estimate: Estimate;
}

export default function EstimateCard({ estimate }: EstimateCardProps) {
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-900/30 text-yellow-400 border-yellow-700/40',
    reviewed: 'bg-blue-900/30 text-blue-400 border-blue-700/40',
    contacted: 'bg-green-900/30 text-green-400 border-green-700/40',
  };

  return (
    <>
      <div className="bg-[#111111] border border-yellow-700/20 rounded-xl overflow-hidden">
        {/* Card Header */}
        <div
          className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors duration-200"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-900/20 border border-yellow-700/30 flex-shrink-0">
              <i className="ri-user-line text-yellow-500"></i>
            </div>
            <div>
              <div className="text-white font-medium text-sm">{estimate.full_name}</div>
              <div className="text-white/40 text-xs mt-0.5">{formatDate(estimate.created_at)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {estimate.signed_photo_urls.length > 0 && (
              <span className="text-yellow-500/70 text-xs flex items-center gap-1">
                <i className="ri-image-line"></i>
                {estimate.signed_photo_urls.length}
              </span>
            )}
            <span className={`text-xs px-2.5 py-1 rounded-full border ${statusColors[estimate.status] || statusColors.pending}`}>
              {estimate.status}
            </span>
            <div className="w-5 h-5 flex items-center justify-center">
              {expanded ? <i className="ri-arrow-up-s-line text-white/40"></i> : <i className="ri-arrow-down-s-line text-white/40"></i>}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="px-6 pb-6 border-t border-yellow-700/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="text-yellow-600/70 text-xs tracking-widest uppercase mb-3">Contact Info</h4>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-phone-line text-yellow-500/60 text-sm"></i>
                  </div>
                  <a href={`tel:${estimate.phone}`} className="text-white text-sm hover:text-yellow-400 transition-colors cursor-pointer">
                    {estimate.phone}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-mail-line text-yellow-500/60 text-sm"></i>
                  </div>
                  <a href={`mailto:${estimate.email}`} className="text-white text-sm hover:text-yellow-400 transition-colors cursor-pointer break-all">
                    {estimate.email}
                  </a>
                </div>
                {estimate.vehicle && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-car-line text-yellow-500/60 text-sm"></i>
                    </div>
                    <span className="text-white text-sm">{estimate.vehicle}</span>
                  </div>
                )}
                {estimate.insurance && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-shield-check-line text-yellow-500/60 text-sm"></i>
                    </div>
                    <span className="text-white text-sm">{estimate.insurance}</span>
                  </div>
                )}
              </div>

              {/* Message */}
              {estimate.message && (
                <div>
                  <h4 className="text-yellow-600/70 text-xs tracking-widest uppercase mb-3">Message</h4>
                  <p className="text-white/60 text-sm leading-relaxed bg-[#1a1a1a] rounded-lg p-4 border border-yellow-700/10">
                    {estimate.message}
                  </p>
                </div>
              )}
            </div>

            {/* Photos */}
            {estimate.signed_photo_urls.length > 0 && (
              <div className="mt-6">
                <h4 className="text-yellow-600/70 text-xs tracking-widest uppercase mb-3">
                  Damage Photos ({estimate.signed_photo_urls.length})
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {estimate.signed_photo_urls.map((url, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-lg overflow-hidden border border-yellow-700/20 cursor-pointer hover:border-yellow-500 transition-colors duration-200"
                      onClick={() => setLightboxPhoto(url)}
                    >
                      <img src={url} alt={`Damage ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-yellow-700/10">
              <a
                href={`tel:${estimate.phone}`}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-xs text-yellow-400 border border-yellow-700/40 rounded-lg hover:bg-yellow-900/20 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line"></i> Call Client
              </a>
              <a
                href={`mailto:${estimate.email}?subject=Your Estimate - The Royal Collision Centre`}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-xs text-yellow-400 border border-yellow-700/40 rounded-lg hover:bg-yellow-900/20 transition-colors cursor-pointer"
              >
                <i className="ri-mail-send-line"></i> Send Email
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            className="whitespace-nowrap absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => setLightboxPhoto(null)}
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <img
            src={lightboxPhoto}
            alt="Damage photo"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
